import React, { useState } from "react";
import { Mail, MapPin, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { storeApi } from "@/lib/storefront";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) { toast.error("Please fill required fields"); return; }
    setSending(true);
    try {
      await storeApi.post("/contact", form);
      toast.success("Message sent — check your inbox for confirmation");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-16 md:py-24" data-testid="contact-page">
      <div className="grid gap-14 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#2D2823]">Contact</div>
          <h1 className="font-display text-5xl leading-tight md:text-[72px]">
            We'd love to <span className="font-display-soft">hear</span> from you
          </h1>
          <p className="mt-5 text-[#2D2823]">Questions, ideas, collaborations — we read every message and reply within 1–2 business days.</p>

          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-center gap-3">
              <Mail size={16} className="text-[#7D8471]" />
              <a href="mailto:info@braitlane.com" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="contact-email">info@braitlane.com</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 flex-shrink-0 text-[#7D8471]" />
              <address className="not-italic text-[#1A1A1A]" data-testid="contact-address">
                Brightlane LLC<br />
                30 N Gould St, Ste 89712<br />
                Sheridan, WY 82801, USA
              </address>
            </li>
          </ul>

          <div className="mt-8 rounded-md border border-[#DDD6C5] bg-[#F7F2E5] p-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[#2D2823]">Prefer to email directly?</div>
            <a href="mailto:info@braitlane.com" className="mt-1 block font-display text-xl text-[#1A1A1A] hover:text-[#7D8471]">
              info@braitlane.com
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-md border border-[#DDD6C5] bg-white p-6 md:col-span-3 md:p-10">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email *" value={form.email} type="email" onChange={(v) => setForm({ ...form, email: v })} required />
            <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} className="md:col-span-2" />
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#2D2823]">Message *</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                className="w-full rounded-md border border-[#DDD6C5] bg-[#F2EDDF] px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]"
              />
            </div>
          </div>
          <button type="submit" disabled={sending} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] px-7 py-3.5 text-sm text-white hover:bg-[#7D8471]">
            {sending ? "Sending…" : <>Send message <Send size={14} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#2D2823]">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full rounded-md border border-[#DDD6C5] bg-[#F2EDDF] px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]" />
    </div>
  );
}
